import { Table, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Button as BootstrapButton, Card, Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  fetchEmployeePendingRequests,
  handleEmployeeRequestAction,
  getEmployeeDetails,
} from "../../../../services/EmployeeDetailServices";
import { decrypt } from "../../../../utils/crypto";
import TooltipWrapper from "../../../../utils/TooltipWrapper";
import config from "../../../../config";
import Loading from "../../../../components/Loading";

const EmployeeRequestDetail = () => {
  const { t } = useTranslation();
  const { emp_id } = useParams();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionData, setActionData] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    if (emp_id) {
      try {
        const decryptedId = decrypt(emp_id);
        setEmployeeId(decryptedId);
      } catch (error) {
        console.error("Error decrypting employee ID:", error);
        toast.error(t("Invalid employee ID"));
        navigate("/employee-request");
      }
    }
  }, [emp_id, navigate, t]);

  // Fetch employee details
  useEffect(() => {
    const fetchEmployeeInfo = async () => {
      if (!employeeId) return;
      setLoadingEmp(true);
      try {
        const response = await getEmployeeDetails(employeeId);
        if (response?.results) {
          const emp = response.results;
          const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();
          setEmployeeName(fullName);
        }
      } catch (error) {
        console.error("Error fetching employee details:", error);
      } finally {
        setLoadingEmp(false);
      }
    };

    fetchEmployeeInfo();
  }, [employeeId]);

  const fetchRequests = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    try {
      const params = {
        page: pagination.current_page,
        limit: pagination.per_page,
      };
      const response = await fetchEmployeePendingRequests(employeeId, params);
      if (response) {
        const requestData = response.data || [];
        setRequests(requestData);

        if (response.pagination) {
          const apiTotal = response.pagination.total || 0;
          const actualTotal = apiTotal > 0 ? apiTotal : requestData.length;
          const totalPages =
            response.pagination.totalPages ||
            Math.ceil(actualTotal / pagination.per_page) ||
            1;

          setPagination((prev) => ({
            ...prev,
            total: actualTotal,
            total_pages: totalPages,
          }));
        }
      } else {
        console.error("No response from API");
        setRequests([]);
      }
    } catch (error) {
      console.error("Error fetching employee requests:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error(t("Failed to fetch employee requests"));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, pagination.current_page, pagination.per_page, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updatePagination = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current_page: parseInt(page) || 1,
      per_page: pageSize || prev.per_page,
    }));
  };

  const handleAction = (record, action) => {
    setActionData({
      id: record.id,
      type: record.request_type,
      action: action,
      record: record,
    });
    setShowConfirmModal(true);
  };

  const confirmAction = async () => {
    if (!actionData) return;

    setActionLoading(true);
    try {
      const payload = {
        type: actionData.type,
        action: actionData.action,
        ids: [actionData.id],
      };

      console.log("Submitting action:", payload);
      const response = await handleEmployeeRequestAction(payload);

      if (response?.success) {
        toast.success(
          t(
            `Request ${actionData.action === "accept" ? "accepted" : "rejected"
            } successfully`,
          ),
        );
        setShowConfirmModal(false);
        setActionData(null);
        // Refresh the list
        fetchRequests();
      } else {
        toast.error(response?.message || t("Failed to process request"));
      }
    } catch (error) {
      console.error("Error processing request action:", error);
      toast.error(
        error.response?.data?.message || t("Failed to process request"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const cancelAction = () => {
    setShowConfirmModal(false);
    setActionData(null);
  };

  const getRequestTypeBadge = (type) => {
    const typeMap = {
      leave: { color: "blue", label: "Leave" },
      attendance: { color: "green", label: "Attendance" },
      reimbursement: { color: "orange", label: "Reimbursement" },
      work_from_home: { color: "purple", label: "Work From Home" },
      work_mode: { color: "geekblue", label: "Work Mode" },
    };

    const normalizedType = String(type || "")
      .trim()
      .toLowerCase();

    const humanizeType = (value) =>
      String(value || "-")
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());

    const config = typeMap[normalizedType] || {
      color: "default",
      label: humanizeType(type),
    };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const normalizeStatus = (value) => {
    if (value === null || value === undefined || value === "") {
      return "pending";
    }

    const numericStatusMap = {
      1: "pending",
      2: "approved",
      3: "rejected",
    };

    if (typeof value === "number") {
      return numericStatusMap[value] || String(value);
    }

    if (typeof value === "string") {
      const trimmedStatus = value.trim().toLowerCase();

      if (numericStatusMap[trimmedStatus]) {
        return numericStatusMap[trimmedStatus];
      }

      if (trimmedStatus === "accept") return "approved";
      if (trimmedStatus === "approve") return "approved";
      if (trimmedStatus === "reject") return "rejected";

      return trimmedStatus;
    }

    return String(value).toLowerCase();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { color: "warning", label: "Pending" },
      approved: { color: "success", label: "Approved" },
      rejected: { color: "danger", label: "Rejected" },
    };
    const normalizedStatus = normalizeStatus(status);
    const config = statusMap[normalizedStatus] || {
      color: "secondary",
      label: String(status),
    };
    return <span className={`badge bg-${config.color}`}>{config.label}</span>;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const columns = [
    {
      title: t("S.No"),
      key: "serial",
      width: 80,
      render: (_, __, index) => (
        <span>
          {(pagination.current_page - 1) * pagination.per_page + index + 1}
        </span>
      ),
    },
    {
      title: t("Request Type"),
      dataIndex: "request_type",
      key: "request_type",
      width: 150,
      render: (type) => getRequestTypeBadge(type),
    },
    {
      title: t("Details"),
      key: "details",
      render: (_, record) => {
        if (record.request_type === "leave") {
          return (
            <div>
              <div className="fw-semibold">{record.leave_type}</div>
              <div className="text-muted small">
                {formatDate(record.start_date)} - {formatDate(record.end_date)}
              </div>
              <div className="text-muted small">{record.leave_days} days</div>
              {record.leave_reason && (
                <div className="text-muted small">
                  Reason: {record.leave_reason}
                </div>
              )}
            </div>
          );
        }
        if (record.request_type === "attendance") {
          return (
            <div>
              <div className="fw-semibold">Date: {formatDate(record.date)}</div>
              <div className="text-muted small">
                In: {new Date(record.punchIn).toLocaleTimeString()} | Out:{" "}
                {new Date(record.punchOut).toLocaleTimeString()}
              </div>
              {record.reason && (
                <div className="text-muted small">Reason: {record.reason}</div>
              )}
            </div>
          );
        }
        if (record.request_type === "reimbursement") {
          return (
            <div>
              <div className="fw-semibold">{record.expense_type}</div>
              <div className="text-muted small">
                Amount: {record.currency?.toUpperCase()} {record.expense_amount}
              </div>
              <div className="text-muted small">
                Date: {formatDate(record.expense_date)}
              </div>
              {record.attachment && (
                <div className="text-muted small">
                  <a
                    href={`${config.IMAGE_URL}${record.attachment}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none"
                  >
                    <i className="fa fa-paperclip me-1" />
                    View Attachment
                  </a>
                </div>
              )}
            </div>
          );
        }

        const normalizedRequestType = String(record.request_type || "")
          .trim()
          .toLowerCase();
        if (
          normalizedRequestType === "work_from_home" ||
          normalizedRequestType === "work_mode"
        ) {

          return (
            <div>
              {record.work_location_name && (
                <div className="fw-semibold">
                  Work Location: {record.work_location_name}
                </div>
              )}
              <div className="text-muted small">
                Request Date: {formatDate(record.work_date)}
              </div>
              
              {record.reason && (
                <div className="text-muted small">Reason: {record.reason}</div>
              )}
            </div>
          );
        }

        return "-";
      },
    },
    {
      title: t("Message"),
      dataIndex: "message",
      key: "message",
      render: (message) => {
        if (!message) return "-";
        const maxLength = 40;
        if (message.length > maxLength) {
          return <span title={message}>{message.slice(0, maxLength)}...</span>;
        }
        return message;
      },
    },
    {
      title: t("Status"),
      key: "status",
      width: 120,
      render: (_, record) => {
        const status = normalizeStatus(
          record.leave_status ?? record.status ?? record.expense_status,
        );
        return getStatusBadge(status);
      },
    },
    {
      title: t("Apply Date"),
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 180,
      render: (_, record) => {
        const status = normalizeStatus(
          record.leave_status ?? record.status ?? record.expense_status,
        );
        const isPending = status === "pending";

        if (!isPending) {
          return (
            <span className="badge bg-secondary">
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        }

        return (
          <div className="btn-group" role="group">
            <TooltipWrapper tooltipText={t("Accept")}>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleAction(record, "accept")}
                disabled={!isPending}
                type="button"
                style={{ opacity: isPending ? 1 : 0.5 }}
              >
                <i className="fa fa-check" />
              </button>
            </TooltipWrapper>
            <TooltipWrapper tooltipText={t("Reject")}>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => handleAction(record, "reject")}
                disabled={!isPending}
                type="button"
                style={{ opacity: isPending ? 1 : 0.5 }}
              >
                <i className="fa fa-times" />
              </button>
            </TooltipWrapper>
          </div>
        );
      },
    },
  ];

  if (loadingEmp) {
    <Loading size="xl" variant="dots" text="" />
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={"Requests - " + employeeName}
          title={t("Pending Requests")}
          titleLink="employee-request"
          page="employee-request"
          subtitle={employeeName}
        />

        <Card>
          <Card.Header className="px-3 pt-3 pb-0">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">{t("Pending Requests")}</h5>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-2 p-md-3">
            <div className="table-responsive">
              <Table
                className="table-striped"
                columns={columns}
                dataSource={requests}
                loading={loading}
                rowKey={(record) => `${record.request_type}-${record.id}`}
                pagination={false}
              />
            </div>

            {/* Custom Pagination */}
            <div
              className="d-flex justify-content-between align-items-center mt-3 px-3 py-2"
              style={{
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 8px 8px",
                minHeight: "50px",
              }}
            >
              {/* LEFT SIDE - Total count */}
              <div className="d-flex align-items-center">
                <div
                  className="pagination-info-box"
                  style={{
                    background: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    color: "#666",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    fontWeight: "500",
                  }}
                >
                  {pagination.total > 0
                    ? `${(pagination.current_page - 1) * pagination.per_page + 1
                    }-${Math.min(
                      pagination.current_page * pagination.per_page,
                      pagination.total,
                    )} of ${pagination.total} requests`
                    : "0 requests"}
                </div>
              </div>

              {/* RIGHT SIDE - Pagination controls */}
              <div className="d-flex align-items-center gap-3">
                {/* Page size selector */}
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#666",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pagination.per_page} / page
                  </span>

                  <select
                    className="form-select form-select-sm"
                    value={pagination.per_page}
                    onChange={(e) =>
                      updatePagination(1, parseInt(e.target.value))
                    }
                    style={{
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                      padding: "6px 10px",
                      fontSize: "14px",
                      width: "110px",
                      height: "32px",
                    }}
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                </div>

                {/* Page navigation */}
                <div className="d-flex align-items-center gap-1">
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page <= 1}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page - 1,
                        pagination.per_page,
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-left"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>

                  {Array.from(
                    { length: Math.min(5, pagination.total_pages) },
                    (_, i) => {
                      const startPage = Math.max(
                        1,
                        pagination.current_page - 2,
                      );
                      const pageNum = startPage + i;

                      if (pageNum > pagination.total_pages) return null;

                      return (
                        <button
                          key={pageNum}
                          className={`btn btn-sm d-flex align-items-center justify-content-center ${pageNum === pagination.current_page
                            ? "btn-primary"
                            : "btn-outline-secondary"
                            }`}
                          onClick={() =>
                            updatePagination(pageNum, pagination.per_page)
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            fontSize: "14px",
                            fontWeight:
                              pageNum === pagination.current_page
                                ? "600"
                                : "400",
                            border: "1px solid #d9d9d9",
                            borderRadius: "4px",
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    },
                  )}

                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center"
                    disabled={pagination.current_page >= pagination.total_pages}
                    onClick={() =>
                      updatePagination(
                        pagination.current_page + 1,
                        pagination.per_page,
                      )
                    }
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: "0",
                      border: "1px solid #d9d9d9",
                      borderRadius: "4px",
                    }}
                  >
                    <i
                      className="fa fa-chevron-right"
                      style={{ fontSize: "12px" }}
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={cancelAction} centered size="md">
          <Modal.Header
            closeButton
            className="border-0 pb-2"
            style={{ backgroundColor: "#fff" }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "20px", fontWeight: "600" }}
            >
              <i
                className={`fa ${actionData?.action === "accept"
                  ? "fa-check-circle text-success"
                  : "fa-times-circle text-danger"
                  }`}
                style={{ fontSize: "22px" }}
              ></i>
              <span>
                {actionData?.action === "accept"
                  ? t("Approve Request")
                  : t("Reject Request")}
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-3 pb-4" style={{ backgroundColor: "#fff" }}>
            <p
              className="mb-4"
              style={{ fontSize: "15px", color: "#6c757d", lineHeight: "1.6" }}
            >
              {t("Are you sure you want to")}{" "}
              <strong
                className={
                  actionData?.action === "accept"
                    ? "text-success"
                    : "text-danger"
                }
              >
                {actionData?.action === "accept" ? t("approve") : t("reject")}
              </strong>{" "}
              {t("this request")}?
            </p>
            {actionData?.record && (
              <div style={{ backgroundColor: "#fff" }}>
                {/* Request Type */}
                <div
                  className="mb-3 pb-3 d-flex"
                  style={{ borderBottom: "1px solid #e9ecef" }}
                >
                  <div
                    className="text-muted small"
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      width: "140px",
                      flexShrink: 0,
                    }}
                  >
                    {t("Request Type")}
                  </div>
                  <div className="flex-grow-1">
                    {getRequestTypeBadge(actionData.type)}
                  </div>
                </div>

                {/* Employee Name */}
                {employeeName && (
                  <div
                    className="mb-3 pb-3 d-flex"
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    <div
                      className="text-muted small"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                        flexShrink: 0,
                      }}
                    >
                      {t("Employee Name")}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        fontSize: "15px",
                        fontWeight: "500",
                        color: "#212529",
                      }}
                    >
                      {employeeName}
                    </div>
                  </div>
                )}

                {/* Work Mode Details */}
                {[
                  "work_mode",
                  "work_from_home",
                ].includes(String(actionData.type || "").trim().toLowerCase()) && (
                  <>
                    <div
                      className="mb-3 pb-3 d-flex"
                      style={{ borderBottom: "1px solid #e9ecef" }}
                    >
                      <div
                        className="text-muted small"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          width: "140px",
                          flexShrink: 0,
                        }}
                      >
                        {t("Work Location")}
                      </div>
                      <div
                        className="flex-grow-1"
                        style={{
                          fontSize: "15px",
                          fontWeight: "500",
                          color: "#212529",
                        }}
                      >
                        {actionData.record.work_location_name || actionData.record.work_location || "-"}
                      </div>
                    </div>

                    <div
                      className="mb-3 pb-3 d-flex"
                      style={{ borderBottom: "1px solid #e9ecef" }}
                    >
                      <div
                        className="text-muted small"
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          width: "140px",
                          flexShrink: 0,
                        }}
                      >
                        {t("Request Date")}
                      </div>
                      <div
                        className="flex-grow-1"
                        style={{
                          fontSize: "15px",
                          fontWeight: "500",
                          color: "#212529",
                        }}
                      >
                        {formatDate(actionData.record.work_date || actionData.record.date)}
                      </div>
                    </div>
                  </>
                )}

                {/* Leave Details */}
                {actionData.type === "leave" && (
                  <div
                    className="mb-3 pb-3 d-flex"
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    <div
                      className="text-muted small"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                        flexShrink: 0,
                      }}
                    >
                      {t("Leave Details")}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{ fontSize: "14px", lineHeight: "1.8" }}
                    >
                      <div className="mb-1">
                        <strong style={{ color: "#212529" }}>
                          {actionData.record.leave_type}
                        </strong>
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        {formatDate(actionData.record.start_date)} -{" "}
                        {formatDate(actionData.record.end_date)}
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        {actionData.record.leave_days} {t("days")}
                      </div>
                    </div>
                  </div>
                )}

                {/* Reimbursement Details */}
                {actionData.type === "reimbursement" && (
                  <div
                    className="mb-3 pb-3 d-flex"
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    <div
                      className="text-muted small"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                        flexShrink: 0,
                      }}
                    >
                      {t("Expense Details")}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{ fontSize: "14px", lineHeight: "1.8" }}
                    >
                      <div className="mb-1">
                        <strong style={{ color: "#212529" }}>
                          {actionData.record.expense_type}
                        </strong>
                      </div>
                      <div className="mb-1">
                        <span
                          className="text-success"
                          style={{ fontSize: "16px", fontWeight: "600" }}
                        >
                          {actionData.record.currency?.toUpperCase()}{" "}
                          {actionData.record.expense_amount}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        {formatDate(actionData.record.expense_date)}
                      </div>
                      {actionData.record.attachment && (
                        <div className="mt-2">
                          <a
                            href={`${config.IMAGE_URL}${actionData.record.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: "12px" }}
                          >
                            <i className="fa fa-paperclip me-1" />
                            View Attachment
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Attendance Details */}
                {actionData.type === "attendance" && (
                  <div
                    className="mb-3 pb-3 d-flex"
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    <div
                      className="text-muted small"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                        flexShrink: 0,
                      }}
                    >
                      {t("Attendance Details")}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{ fontSize: "14px", lineHeight: "1.8" }}
                    >
                      <div
                        className="mb-1 text-muted"
                        style={{ fontSize: "13px" }}
                      >
                        {formatDate(actionData.record.date)}
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        In:{" "}
                        {new Date(
                          actionData.record.punchIn,
                        ).toLocaleTimeString()}
                      </div>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        Out:{" "}
                        {new Date(
                          actionData.record.punchOut,
                        ).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Message */}
                {actionData.record.message && (
                  <div className="mb-0 d-flex">
                    <div
                      className="text-muted small"
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        width: "140px",
                        flexShrink: 0,
                      }}
                    >
                      {t("Message")}
                    </div>
                    <div className="flex-grow-1">
                      {actionData.record.message}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer
            className="border-0 pt-0 pb-4"
            style={{ backgroundColor: "#fff" }}
          >
            <BootstrapButton
              variant="light"
              onClick={cancelAction}
              disabled={actionLoading}
              className="px-4 py-2"
              style={{
                fontWeight: "500",
                fontSize: "14px",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
              }}
            >
              {t("Cancel")}
            </BootstrapButton>
            <BootstrapButton
              variant={actionData?.action === "accept" ? "success" : "danger"}
              onClick={confirmAction}
              disabled={actionLoading}
              className="px-4 py-2"
              style={{
                fontWeight: "500",
                fontSize: "14px",
                borderRadius: "6px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {actionLoading ? (
                <span className="d-flex align-items-center gap-2">
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ width: "1rem", height: "1rem" }}
                    role="status"
                    aria-hidden="true"
                  />
                  <span>{t("Processing...")}</span>
                </span>
              ) : (
                <span className="d-flex align-items-center gap-2">
                  <i
                    className={`fa ${actionData?.action === "accept" ? "fa-check" : "fa-times"
                      }`}
                    aria-hidden="true"
                  />
                  <span>
                    {actionData?.action === "accept"
                      ? t("Approve")
                      : t("Reject")}
                  </span>
                </span>
              )}
            </BootstrapButton>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeRequestDetail;
